import { formatDate, parseDate } from "@sembark-travel/datetime-utils"

// The pure half of the policy version diff: no React, so the component file
// (`PolicyDiff.tsx`) exports only components.

// How one row of a version diff moved between the two versions. The additional
// info, cancellation and payment policy diffs all report it.
export type TDiffStatus = "unchanged" | "changed" | "added" | "removed"

export type TDiffSide = "previous" | "current"

// Diff entries of the same date range are ordered changed/unchanged, then
// added, then removed, so a range that moved reads as "this one went away"
// after "this one arrived".
export const DIFF_STATUS_RANK: Record<TDiffStatus, number> = {
	unchanged: 0,
	changed: 0,
	added: 1,
	removed: 2,
}

export type TPolicyTextDiffEntry = {
	key: string
	previous: string | null
	current: string | null
	status: TDiffStatus
}

export function diffMarkdownText(
	previous: string | null | undefined,
	current: string | null | undefined
): TPolicyTextDiffEntry {
	const prevClean = (previous || "").replace(/\r\n/g, "\n").trim()
	const currClean = (current || "").replace(/\r\n/g, "\n").trim()

	const hasPrev = Boolean(prevClean)
	const hasCurr = Boolean(currClean)

	if (!hasPrev && !hasCurr) {
		return {
			key: "additional-information",
			previous: null,
			current: null,
			status: "unchanged",
		}
	}

	if (!hasPrev && hasCurr) {
		return {
			key: "additional-information",
			previous: null,
			current: currClean,
			status: "added",
		}
	}

	if (hasPrev && !hasCurr) {
		return {
			key: "additional-information",
			previous: prevClean,
			current: null,
			status: "removed",
		}
	}

	if (prevClean === currClean) {
		return {
			key: "additional-information",
			previous: prevClean,
			current: currClean,
			status: "unchanged",
		}
	}

	return {
		key: "additional-information",
		previous: prevClean,
		current: currClean,
		status: "changed",
	}
}

// ---------------------------------------------------------------------------
// Rule diff
//
// Both policy kinds hold their rules the same way: a base rule, which applies
// whenever no date range matches, then one rule per check-in date range, each
// carrying a list of items (penalty conditions, payment rules). Two versions go
// in, a flat list of side-by-side diff entries comes out; each module only says
// how its items are keyed.
// ---------------------------------------------------------------------------

export type TDiffRow<T> = {
	key: string
	previous: T | null
	current: T | null
	status: TDiffStatus
}

export type TRuleBlockDiffEntry<TBlock, TItem> = {
	key: string
	isBase: boolean
	previous: TBlock | null
	current: TBlock | null
	status: TDiffStatus
	items: Array<TDiffRow<TItem>>
}

type TDateRangeRule = {
	valid_from: string | null
	valid_to: string | null
}

export function isBaseDateRangeRule(rule: TDateRangeRule): boolean {
	return rule.valid_from == null && rule.valid_to == null
}

// Rules pair across versions by their check-in date range. Both ends are
// normalized to plain dates first, so a change in the api's timestamp
// precision cannot turn one rule into a removed + added pair.
export function dateRangeRuleKey(rule: TDateRangeRule): string {
	if (isBaseDateRangeRule(rule)) return "base"
	const from = rule.valid_from
		? formatDate(parseDate(rule.valid_from), "YYYY-MM-DD")
		: ""
	const to = rule.valid_to
		? formatDate(parseDate(rule.valid_to), "YYYY-MM-DD")
		: ""
	return `${from}|${to}`
}

export function formatDateRangeRuleLabel(rule: TDateRangeRule): string {
	if (isBaseDateRangeRule(rule)) return "Base Rules"
	const from = rule.valid_from
		? formatDate(parseDate(rule.valid_from), "DD MMM, YYYY")
		: "—"
	const to = rule.valid_to
		? formatDate(parseDate(rule.valid_to), "DD MMM, YYYY")
		: "—"
	return `${from} → ${to} (Check-in)`
}

// Longest common subsequence over the item keys. The lists are a handful of
// items long, so the quadratic table is free and gives us the shortest
// possible diff: items that did not change stay aligned and neutral, which is
// the whole point of the side-by-side view.
export function diffListByKey<T>(
	previous: ReadonlyArray<T>,
	current: ReadonlyArray<T>,
	keyOf: (item: T) => string
): Array<TDiffRow<T>> {
	const previousKeys = previous.map(keyOf)
	const currentKeys = current.map(keyOf)
	const n = previous.length
	const m = current.length

	// lcs[i][j] = length of the longest common subsequence of previous[i..] and
	// current[j..], filled from the end so the walk below can go forwards.
	const lcs: Array<Array<number>> = Array.from({ length: n + 1 }, () =>
		new Array<number>(m + 1).fill(0)
	)
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			lcs[i][j] =
				previousKeys[i] === currentKeys[j]
					? lcs[i + 1][j + 1] + 1
					: Math.max(lcs[i + 1][j], lcs[i][j + 1])
		}
	}

	const rows: Array<TDiffRow<T>> = []
	let pendingPrevious: Array<T> = []
	let pendingCurrent: Array<T> = []

	function pushRow(
		previousItem: T | null,
		currentItem: T | null,
		status: TDiffStatus
	) {
		// Identical items can legitimately appear twice in one list, so the
		// position has to be part of the react key.
		const previousKey = previousItem ? keyOf(previousItem) : ""
		const currentKey = currentItem ? keyOf(currentItem) : ""
		rows.push({
			key: `${rows.length}-${status}-${previousKey}-${currentKey}`,
			previous: previousItem,
			current: currentItem,
			status,
		})
	}

	// A run of removals next to a run of additions is what an edit looks like,
	// so zip them into "changed" rows: old on the left, new on the right.
	// Whatever is left over on either side stands alone.
	function flush() {
		const length = Math.max(pendingPrevious.length, pendingCurrent.length)
		for (let k = 0; k < length; k++) {
			const previousItem = pendingPrevious[k] || null
			const currentItem = pendingCurrent[k] || null
			pushRow(
				previousItem,
				currentItem,
				previousItem && currentItem
					? "changed"
					: previousItem
						? "removed"
						: "added"
			)
		}
		pendingPrevious = []
		pendingCurrent = []
	}

	let i = 0
	let j = 0
	while (i < n || j < m) {
		if (i < n && j < m && previousKeys[i] === currentKeys[j]) {
			flush()
			pushRow(previous[i], current[j], "unchanged")
			i++
			j++
		} else if (i === n || (j < m && lcs[i][j + 1] >= lcs[i + 1][j])) {
			pendingCurrent.push(current[j])
			j++
		} else {
			pendingPrevious.push(previous[i])
			i++
		}
	}
	flush()

	return rows
}

// Pairs the rules of two versions, diffs the items of each pair and returns the
// entries that changed.
export function diffDateRangeRules<
	TBlock extends TDateRangeRule & { id: number },
	TItem,
>(
	previousRules: ReadonlyArray<TBlock>,
	currentRules: ReadonlyArray<TBlock>,
	diffItems: (
		previous: TBlock | null,
		current: TBlock | null
	) => Array<TDiffRow<TItem>>
): Array<TRuleBlockDiffEntry<TBlock, TItem>> {
	const entries: Array<TRuleBlockDiffEntry<TBlock, TItem>> = []

	function pushEntry(
		previous: TBlock | null,
		current: TBlock | null,
		isBase: boolean
	) {
		// A removed rule is just a diff against nothing, and an added one a diff
		// from nothing, so all three cases share this single call.
		const items = diffItems(previous, current)
		const status: TDiffStatus = !previous
			? "added"
			: !current
				? "removed"
				: items.every((item) => item.status === "unchanged")
					? "unchanged"
					: "changed"
		const rangeKey = dateRangeRuleKey(
			current || previous || { valid_from: null, valid_to: null }
		)
		entries.push({
			key: `${status}-${rangeKey}-${previous?.id || ""}-${current?.id || ""}`,
			isBase,
			previous,
			current,
			status,
			items,
		})
	}

	const previousBase = previousRules.find(isBaseDateRangeRule) || null
	const currentBase = currentRules.find(isBaseDateRangeRule) || null
	if (previousBase || currentBase) {
		pushEntry(previousBase, currentBase, true)
	}

	// Date rules pair on their range. Buckets keep the original order, so when
	// a version repeats the same range the duplicates pair up in order.
	const previousByRange = new Map<string, Array<TBlock>>()
	for (const rule of previousRules) {
		if (isBaseDateRangeRule(rule)) continue
		const key = dateRangeRuleKey(rule)
		const bucket = previousByRange.get(key)
		if (bucket) {
			bucket.push(rule)
		} else {
			previousByRange.set(key, [rule])
		}
	}

	for (const rule of currentRules) {
		if (isBaseDateRangeRule(rule)) continue
		const matched = previousByRange.get(dateRangeRuleKey(rule))?.shift() || null
		pushEntry(matched, rule, false)
	}

	// Anything still in a bucket existed in the older version only.
	for (const bucket of previousByRange.values()) {
		for (const rule of bucket) {
			pushEntry(rule, null, false)
		}
	}

	return sortRuleBlockDiffEntries(entries).filter(
		(entry) => entry.status !== "unchanged"
	)
}

// Base rule first, then date rules chronologically, then by `DIFF_STATUS_RANK`.
function sortRuleBlockDiffEntries<
	TEntry extends TRuleBlockDiffEntry<TDateRangeRule, unknown>,
>(entries: Array<TEntry>): Array<TEntry> {
	return entries
		.map((entry, index) => ({ entry, index }))
		.sort((a, b) => {
			if (a.entry.isBase !== b.entry.isBase) return a.entry.isBase ? -1 : 1
			const aRule = a.entry.current || a.entry.previous
			const bRule = b.entry.current || b.entry.previous
			const fromDiff = String(aRule?.valid_from || "").localeCompare(
				String(bRule?.valid_from || "")
			)
			if (fromDiff !== 0) return fromDiff
			const toDiff = String(aRule?.valid_to || "").localeCompare(
				String(bRule?.valid_to || "")
			)
			if (toDiff !== 0) return toDiff
			const rankDiff =
				DIFF_STATUS_RANK[a.entry.status] - DIFF_STATUS_RANK[b.entry.status]
			if (rankDiff !== 0) return rankDiff
			return a.index - b.index
		})
		.map(({ entry }) => entry)
}

export type TDiffTone = "removed" | "added" | null

export function diffTone(status: TDiffStatus, side: TDiffSide): TDiffTone {
	if (status === "unchanged") return null
	if (status === "removed") return side === "previous" ? "removed" : null
	if (status === "added") return side === "current" ? "added" : null
	return side === "previous" ? "removed" : "added"
}
